import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { arrayHasItems } from '../../helpers/common';
import {
  DemandPlanningLoadingKeys,
  getBundleDemandExplosionTableData,
} from '../../slices/demandPlanningSlice';
import { LoaderSpinner } from '../LoaderSpinner';
import { BundleDemandExplosionSummaryBar } from './bundleDemandExplosionComponents/BundleDemandExplosionSummaryBar';
import { BundleDemandExplosionBundleRail } from './bundleDemandExplosionComponents/BundleDemandExplosionBundleRail';
import { BundleDemandExplosionMainTable } from './bundleDemandExplosionComponents/BundleDemandExplosionMainTable';
import { BundleDemandExplosionInspectDrawer } from './bundleDemandExplosionComponents/BundleDemandExplosionInspectDrawer';
import {
  EXCEPTION_FILTER_OPTIONS,
  buildBundleExplosionGroups,
  downloadBundleExplosionCsv,
  filterBundleExplosionRows,
  getBundleExplosionExportGuidance,
  getBundleExplosionStatusLabel,
  summarizeBundleExplosionRows,
} from '../../lib/bundleDemandExplosionHelpers';
import { SelectDropdown } from '../SelectDropdown';

const BundleDemandExplosionTable = ({
  selectedForecastDemandPlanning,
  searchTerm,
  selectedCategoryId,
  selectedSubcategoryId,
  selectedStatusId,
  marginFromSidebar,
}) => {
    const dispatch = useDispatch();
    const { bundleDemandExplosionTableData, loadingKeys } = useSelector(state => state.demandPlanning);

    const [localSearch, setLocalSearch] = useState('');
    const [exceptionFilter, setExceptionFilter] = useState('all');
    const [selectedRowId, setSelectedRowId] = useState(null);

    const bundleDemandExplosionTableLoading = loadingKeys.includes(
        DemandPlanningLoadingKeys.getBundleDemandExplosionTableData
    );
    const rows = useMemo(() => {
        const raw = bundleDemandExplosionTableData?.rows;
        return arrayHasItems(raw) ? raw : [];
    }, [bundleDemandExplosionTableData]);

    const filteredRows = useMemo(
        () => filterBundleExplosionRows(rows, { search: localSearch, exceptionFilter }),
        [rows, localSearch, exceptionFilter]
    );

    const summary = useMemo(() => summarizeBundleExplosionRows(filteredRows), [filteredRows]);
    const bundleGroups = useMemo(() => buildBundleExplosionGroups(filteredRows), [filteredRows]);

    const selectedRow = useMemo(
        () =>
            filteredRows.find(row => row.id === selectedRowId) ??
            rows.find(row => row.id === selectedRowId) ??
            null,
        [filteredRows, rows, selectedRowId]
    );

    const selectedExecutionRows = useMemo(() => {
        if (!selectedRow) return [];
        return rows.filter(row => row.executionSku === selectedRow.executionSku);
    }, [rows, selectedRow]);

    const exceptionRowsForExport = useMemo(() => rows.filter(row => row.hasException), [rows]);

    const forecastYear =
        bundleDemandExplosionTableData?.forecastYear ??
        selectedForecastDemandPlanning?.forecast_year ??
        selectedForecastDemandPlanning?.forecastYear;

    useEffect(() => {
        const demandPlanningId = selectedForecastDemandPlanning?.id;
        const demandPlanningYear =
        selectedForecastDemandPlanning?.forecast_year ??
        selectedForecastDemandPlanning?.forecastYear;

        if (demandPlanningId == null || demandPlanningYear == null || demandPlanningYear === '') return;

        setSelectedRowId(null);

        dispatch(
            getBundleDemandExplosionTableData({
                demandPlanningId,
                demandPlanningYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            })
        );
    }, [
        dispatch,
        selectedForecastDemandPlanning?.id,
        selectedForecastDemandPlanning?.forecast_year,
        selectedForecastDemandPlanning?.forecastYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    ]);

    const handleExportExceptions = () => {
        const headers = [
            'Execution SKU',
            'Execution Product',
            'Sub Category',
            'Component SKU',
            'Component Product',
            'Supplier',
            'Units Per Bundle',
            'Forecast Units',
            'Component Units',
            'Exception Type',
            'Resolution Guidance',
        ];

        const exportRows = exceptionRowsForExport.map(row => [
            row.executionSku,
            row.executionProductName,
            row.executionSubCategory,
            row.componentSku,
            row.componentProductName,
            row.supplier,
            row.unitsPerBundle,
            row.forecastUnits,
            row.componentUnits,
            getBundleExplosionStatusLabel(row),
            getBundleExplosionExportGuidance(row),
        ]);

        downloadBundleExplosionCsv('bundle-demand-exceptions', headers, exportRows);
    };

    const handleSelectBundle = executionSku => {
        const firstMatchingRow =
        filteredRows.find(row => row.executionSku === executionSku) ??
        rows.find(row => row.executionSku === executionSku);
        setSelectedRowId(firstMatchingRow?.id ?? null);
    };

    if (bundleDemandExplosionTableLoading) {
        return (
            <section className="flex-1 px-4 py-4 lg:px-6">
                <div className="flex justify-center items-center h-full mt-8">
                    <LoaderSpinner
                        width="80"
                        height="80"
                        border="5"
                        colorSpinner="gray"
                    />
                </div>
            </section>
        );
    }

    return (
        <>
            <section className="mb-4 rounded-lg border border-gray-200 bg-white p-2 shadow-sm lg:p-4">
                <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <span className="mb-2 inline-flex rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-900">
                            Bundle Demand Explosion
                        </span>
                        <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900">
                            Verify how virtual bundle demand propagates into component SKU demand
                        </h2>
                        <p className="mt-1 max-w-3xl text-sm text-gray-500">
                            This view mirrors the workbook&apos;s execution-to-component explosion layer so planners can confirm
                            that every virtual bundle creates the expected component demand before replacement logic and final
                            aggregation.
                            {forecastYear ? ` Forecast units use sales for ${forecastYear}.` : ''}
                        </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                        <input
                            className="h-9 w-full min-w-[180px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 sm:w-[260px]"
                            onChange={event => setLocalSearch(event.target.value)}
                            placeholder="Search bundle, component, or supplier"
                            value={localSearch}
                        />
                        <label>
                            <span className="sr-only">Exception filter</span>
                            <SelectDropdown
                                options={EXCEPTION_FILTER_OPTIONS}
                                value={exceptionFilter}
                                onChange={(value) => setExceptionFilter(value)}
                                placeholder="Exception filter"
                                selectClassname="!h-9 !min-w-[180px] !max-w-[210px] !w-full !bg-transparent !text-sm !outline-none !bg-white"
                                dropdownClassname="!p-1"
                                itemValueClassname="!text-sm !px-1.5 hover:!bg-primary hover:!text-white"
                                selectedItemValueClassname="!bg-primary !text-white"
                                portal={true}
                            />
                        </label>
                        <button
                            type="button"
                            className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={exceptionRowsForExport.length === 0}
                            onClick={handleExportExceptions}
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
                            Export exceptions
                        </button>
                    </div>
                </div>

                <BundleDemandExplosionSummaryBar summary={summary} />

                <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(260px,0.82fr)_minmax(0,1.7fr)]">
                    <BundleDemandExplosionBundleRail
                        bundleGroups={bundleGroups}
                        onSelectBundle={handleSelectBundle}
                    />
                    <BundleDemandExplosionMainTable
                        rows={filteredRows}
                        onInspectRow={setSelectedRowId}
                    />
                </div>
            </section>

            <BundleDemandExplosionInspectDrawer
                open={Boolean(selectedRow)}
                onClose={() => setSelectedRowId(null)}
                selectedRow={selectedRow}
                executionRows={selectedExecutionRows}
                marginFromSidebar={marginFromSidebar}
            />
        </>
    );
};

export default BundleDemandExplosionTable;
